import { VoyageRoutingModelBase, VoyageRoute, RouteCalcStatus, RoutingJobOptions, 
         VoyageRoutePointFeature, VoyageRouteLineFeature, WeatherLimits, CalculationFailedDetails, Backends, NextWaypointResult, NextWaypointPayload, MissingPoints, 
         RouteTypesPayload,
         VoyageSummaryPayload} from './voyage-routing-model';
import { RoutingService } from '../services/routing-service';
import { UiStateModel } from '../models/ui-state-model';

import { LineString } from 'ol/geom';
import { getLineStringForFractionPositions } from '../utils/map-utils';
import { debounce, GetFormattedLocalDate } from '../utils/helpers';
import { WeatherModel } from './weather-model';
import { DefaultShipModel } from "./ship-model";


import { RouteOptimizerService } from "../services/route-optimiser-service";
import { PlanChangedEvent, VesselPlanningModel, VoyagePlan } from './vessel-planning-model';
import { HydraRoutingService } from '../services/hydra-routing-service';
import { RouteWeatherWorker } from '../utils/route-weather-worker';
import { markPassedAndAddCurrentPosition, uiTypeToIndex, verifyRequestedETA } from '../utils/route-utils';
import { WhaleZonesUtilities } from '../utils/zones/whale-zones-utils';
import { EcaSecaAreasUtilities } from '../utils/zones/eca-seca-areas-utils';
import { EncService } from '../services/enc-service';
import { VesselEnpBookAndChapterModel } from './vessel-enp-book-and-chapter-model';
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { importFromFile } from '../converters/converters';
import { ImportResult } from '../converters/types/converters.types';
import { PlansService } from '../services/plans-service';

const _PROJECTION: string = (<any>window)["appenv"].projection;
interface UIRouteMetadata
{
    name: string;
    status: RouteCalcStatus;
    routeIndex: number;
    error: null|string;
    selected: boolean;
    crossingWhaleZones: boolean;
}

interface VesselMovementCacheEntry
{
    startDate: number;
    endDate: number;
    line: LineString;
    projectedLine: LineString;
}

export interface JobDetails
{
    jobId: string|number;
    calcOptions?: RoutingJobOptions
}

export class RouteOptimiserCalcError extends Error
{
    readonly hydraRequestId: string;
    readonly hydraErrorBody: any;
    
    constructor(hydraRequestId: string, hydraErrorBody: any, message: string)
    {
        super(message);

        this.hydraRequestId = hydraRequestId;
        this.hydraErrorBody = hydraErrorBody;
    }
}

export class VoyageRoutingModelImpl extends VoyageRoutingModelBase
{
    shipModel = DefaultShipModel;

    constructor()
    {
        super();

        this._backend = (<any>window)["appenv"].featureToggles.routingBackend || "DTN";

        this._emitRoutesChanged = debounce( () => this.dispatchEvent(new Event('routes-changed')) );
        this._loadWeatherLimits();
        this._checkForURLJobs();

        this._createUIRoutes(3); // Always 3 for now

        this._onPlanChanged = this._onPlanChanged.bind(this);
        if (VesselPlanningModel)
        {
            VesselPlanningModel.addEventListener("plan-changed", <EventListener>this._onPlanChanged);
        }
    }

    get activePlan()
    {
        return this._activePlan;
    }

    get jobCount() {
        return this._jobs.length;
    }

    get routes() {
        return this._routes;
    }

    get uiRoutes()
    {
        const result = [];

        for (const uiRoute of this._uiRoutes)
        {
            result.push({
                name: uiRoute.name,
                status: uiRoute.status,
                route: uiRoute.routeIndex !== -1 ? this.routes[uiRoute.routeIndex] : null,
                error: uiRoute.error,
                selected : uiRoute.selected,
                crossingWhaleZones: uiRoute.crossingWhaleZones,
            });
        }

        return result;
    }

    get routingBackend()
    {
        return this._backend;
    }

    changeRoutingBackend(newBackend: Backends)
    {
        this._backend = newBackend;
    }

    async setActivePlan(scheduleId: string): Promise<void>
    {
        if (!scheduleId)
        {
            this._activePlan = null;
            this._clearUIRoutes();
        }
        else if (scheduleId !== this._activePlan)
        {
            this._activePlan = scheduleId;
            return this._updateRoutesFromPlan();
        }
    }

    addRoute(route: VoyageRoute): number {

        this.routes.push(route);
        this._emitRoutesChanged();

        return this.routes.length - 1;
    }

    getRoute(index: number) {
        return this.routes[index];
    }

    deleteRoute(index: number)
    {
        this.routes.splice(index, 1);

        for (const uiRoute of this._uiRoutes)
        {
            if (uiRoute.routeIndex === index)
            {
                uiRoute.routeIndex = -1;
                uiRoute.status = "idle";
                uiRoute.selected = false;
            }
            else if (uiRoute.routeIndex > index)
            {
                uiRoute.routeIndex--;
            }
        }

        this._emitRoutesChanged();
    }

    async postRoutingJob(startPos: [number, number], endPos: [number, number], options: RoutingJobOptions): Promise<VoyageRoute>
    {
        const route: VoyageRoute = {
            route_geometry: [
                {
                    type: "Feature",
                    geometry: { coordinates: startPos, type: "Point" },
                    properties: { type: "Hard" }
                },
                {
                    type: "Feature",
                    geometry: { coordinates: [startPos, endPos], type: "LineString" },
                    properties: {}
                },
                {
                    type: "Feature",
                    geometry: { coordinates: endPos, type: "Point" },
                    properties: { type: "Hard" }
                }
            ]
        };

        return await this.postRoutingJobUsingRoute(route, options);
    }

    async postRoutingJobUsingRoute(route: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>
    {
        let calculatedRoute: VoyageRoute;

        if (!options.draft && !(options.draftAft && options.draftFore)) {
            throw new Error("Missing draft in input to postRoutingJob");
        }

        options.weatherLimits = options.weatherLimits || this._weatherLimits;

        try
        {
            switch (this._backend)
            {
                case Backends.DTN:
                    calculatedRoute = await this._postRoutingJobToRoutingAPI(route, options);
                    break;

                case Backends.RouteOptimiser:
                    calculatedRoute = await this._postRoutingJobToOptimiser(route, options);
                    break;

                case Backends.Hydra:
                    calculatedRoute = await this._postRoutingJobToHydra(route, options);
                    break;
                
                default:
                    throw new Error("Invalid backend!")
            }
            
            await verifyRequestedETA(calculatedRoute, options.planScheduleId || undefined);
            this._detectZones(calculatedRoute, options.uiRouteIndex!);
            
            return calculatedRoute;
        }
        catch (e)
        {
            console.warn("Calculation failed!", e);

            if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
                this._uiRoutes[options.uiRouteIndex].status = "failed";
                this._uiRoutes[options.uiRouteIndex].error = (<Error>e).message;
                this._emitRoutesChanged();
            }
            
            this.dispatchEvent(new CustomEvent<CalculationFailedDetails>("calculation-failed", {
                detail: {
                    error: (<Error>e).message,
                    routeIndex: options.uiRouteIndex!,
                    scheduleId: options.updatePlan ? options.planScheduleId : undefined,
                    hydraRequestId: e instanceof RouteOptimiserCalcError ? e.hydraRequestId : undefined,
                    hydraErrorBody: e instanceof RouteOptimiserCalcError ? e.hydraErrorBody : undefined
                }
            }));

            throw e;
        }
    }

    async postEncAugmentRouteJob(route: VoyageRoute): Promise<VoyageRoute|null> {
        const routeWithEnc = await EncService.postEncAugmentRouteJob(route);
        return routeWithEnc;
    }

    setWeatherLimits(weatherLimits: WeatherLimits): void
    {
        this._weatherLimits = weatherLimits;
        localStorage.setItem("voyage-routing-model-weather-limits", JSON.stringify(weatherLimits));
        this.dispatchEvent(new Event('weather-limits-changed'));
    }

    getWeatherLimits(): WeatherLimits
    {
        return this._weatherLimits;
    }

    planHasActiveCalculations(scheduleId: string): boolean
    {
        return this._jobs.some( job => job.calcOptions && job.calcOptions.planScheduleId === scheduleId );
    }
   
    async importFilesRaw(files: FileList): Promise<ImportResult[]>
    {
        const promises: Promise<ImportResult>[] = [];

        Array.from(files).forEach(file => {
            promises.push( importFromFile(file) );
        });

        return Promise.all(promises);
    }

    async importFile(file: File, options: RoutingJobOptions, points: MissingPoints | null,  mergeWith?: VoyageRoute, addCurrentPosition?: boolean): Promise<VoyageRoute>
    {
        let imported: ImportResult;

        if (options.updatePlan)
        {
            const plan = await VesselPlanningModel!.getCurrentPlan(options.planScheduleId!);
            if (!plan)
            {
                throw new Error(`No plan exists for ${options.planScheduleId}!`);
            }

            imported = await importFromFile(file, plan, mergeWith);

           if( points && points.beforeFistPilotPoint && points?.afterLastPilotPoint ) {
       
                const beforePP = points.beforeFistPilotPoint.filter(el => !el.properties.pilotPoint );
                const afterPP = points.afterLastPilotPoint.filter(el => !el.properties.pilotPoint );
                const addPP = imported.route.route_geometry.map((el, idx )=> {
      
                    if(idx === 0 || idx ===  imported.route.route_geometry.length -1 )  {
                        const properties = {...el.properties,  pilotPoint: true}
                        return {...el, properties}
                    }
                    return el;
                })
                
                const route = Array.from(new Set([...beforePP, ...addPP, ...afterPP]));

                const route_g = {...imported.route, route_geometry: route}
                imported = {...imported, route: route_g };
           }


            // Apply extras (if any) to plan as well
            if (imported.extras)
            {
                plan.departurePilotPosition = imported.extras.departurePilotPosition || plan.departurePilotPosition;
                plan.arrivalPilotPosition = imported.extras.arrivalPilotPosition || plan.arrivalPilotPosition;
                if (imported.extras.passagePlan)
                {
                    for (const prop in imported.extras.passagePlan)
                    {
                        if (prop in plan.passagePlan)
                        {
                            (<any>plan.passagePlan[<keyof VoyagePlan["passagePlan"]>prop]) = imported.extras.passagePlan[prop];
                        }
                    }
                }
            }

            // Add current position if needed
            if (addCurrentPosition)
            {
                await markPassedAndAddCurrentPosition(imported.route, true, true);
            }

            // Save route to plan now (so we still have it if the calculation fails for some reason)
            if (options.updatePlan.selectRoute === undefined) {
                options.updatePlan.selectRoute = options.updatePlan.routeIndex >= 0 && plan.selectedRouteIndex === options.updatePlan.routeIndex;
            }
            
            if (options.uiRouteIndex) {
                imported.route.uiType = `UserRoute${options.uiRouteIndex}`;
            }
            
            if (options.updatePlan.routeIndex >= 0) {
                plan.routes[options.updatePlan.routeIndex] = imported.route;
                if (plan.selectedRouteIndex === options.updatePlan.routeIndex) {
                    plan.selectedRouteIndex = -1;
                }
            } else {
                plan.routes.push(imported.route);
                options.updatePlan.routeIndex = plan.routes.length-1;
            }

            if (imported.route.passagePlan) {
                options.passagePlan = imported.route.passagePlan;
            }
            
            await VesselPlanningModel!.setPlan(plan.scheduleId, plan);
        }
        else
        {
            imported = await importFromFile(file);
        }

        this._fixupMaxSpeed(imported.route);

        //Add mandatory ECA/SECA waypoints
        const areasUtility: EcaSecaAreasUtilities = new EcaSecaAreasUtilities();
        imported.route = await areasUtility.applyToRoute(imported.route);

        return this.postRoutingJobUsingRoute(imported.route, options);
    }

    async importShipModelFile(file: File): Promise<void> {
        return new Promise( (resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => {
                try {
                    const parsedFile = JSON.parse(<string>fr.result);
                    this.shipModel = {
                        shipProfile: parsedFile.ship_profile,
                        speedRanges: parsedFile.speed_ranges
                    };

                    if (!this.shipModel.shipProfile.ship_dynamics) {
                        // Model files from analytics seems to be missing ship_dynamics; add it here if necessary
                        this.shipModel.shipProfile.ship_dynamics = DefaultShipModel.shipProfile.ship_dynamics;
                    }

                    // Add these if missing (added in v4 of the routing API)
                    if (!this.shipModel.shipProfile.ship_measurements.cb) {
                        this.shipModel.shipProfile.ship_measurements.cb = DefaultShipModel.shipProfile.ship_measurements.cb;
                    }
                    if (this.shipModel.shipProfile.ship_type === undefined) {
                        this.shipModel.shipProfile.ship_type = DefaultShipModel.shipProfile.ship_type;
                    }

                    resolve();
                } catch (err) {
                    reject(err);
                }
            };
            fr.readAsText(file);
        })
    }

    async getNextWaypointIndex(maxAllowedDeviation: number, position: number[], routeGeometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>): Promise<NextWaypointResult> {
        const payload : NextWaypointPayload = { maxAllowedDeviation, position, routeGeometry }
        const result = await RouteOptimizerService.getNextWaypointIndex(payload);
        
        return result;
    }

    getPositionAndHeadingInTime(timestamp: number, route: VoyageRoute) {
        if (!this._vesselMovementCache.has(route)) {
            this._createVesselMovementCacheGeoJson(<VoyageRoute>route);
        }

        const segment = this._vesselMovementCache.get(route)!.find(segment => timestamp >= segment.startDate && segment.endDate > timestamp)
        if (!segment) return null;

        const line = segment.line;

        // ####
        // Note on playback not being in sync i.e. the vessel-in-time icon is not starting exactly on the vessel icon.
        
        // The vessel-in-time icon will never be 100% in sync with actual vessel position because it is synced to hydra 
        // timestamps, and not the actual vessel position.

        // The above segments are based on Hard WPs and informational WPs. Timestamps on these are used to 
        // determine the position of the vessel-in-time icon - not the actual position of the vessel, because
        // this does not exist as a WP on the route. The position of vessel-in-time icon is determined based on
        // the time fraction between two waypoints. E.g., if 20% time has passed between two WPs, then vessel-in-time 
        // icon will be put on 20% along the particular segment (linestring). This might diverge from the actual, last
        // position of the vessel, if the vessel is not following the timestamps (calculated by Hydra).
        // ####
        const fraction = (timestamp - segment.startDate) / (segment.endDate - segment.startDate);
        const position = <[number,number]>line.getCoordinateAt(fraction);

        // Compute the heading by comparing the coordinate with another point on the line close to it
        let headingStart, headingEnd;
        if (fraction > 0.95)
        {
            headingStart = line.getCoordinateAt(fraction - 0.05);
            headingEnd = position;
        }
        else
        {
            headingStart = position;
            headingEnd = line.getCoordinateAt(fraction + 0.05);
        }
        const startLong = headingStart[0] * Math.PI / 180;
        const endLong = headingEnd[0] * Math.PI / 180;
        const startLat = headingStart[1] * Math.PI / 180;
        const endLat = headingEnd[1] * Math.PI / 180;
        const y = Math.sin(endLong-startLong) * Math.cos(endLat);
        const x = Math.cos(startLat)*Math.sin(endLat) - Math.sin(startLat)*Math.cos(endLat)*Math.cos(endLong-startLong);
        const heading = Math.atan2(y, x);

        const projectedPosition = <[number,number]>segment.projectedLine.getCoordinateAt(fraction);

        return {position, projectedPosition, heading};
    }


    private _routes: VoyageRoute[] = [];
    private _jobs: JobDetails[] = [];
    private _activePlan: string|null = null;
    private _uiRoutes: UIRouteMetadata[] = [];
    private _weatherLimits!: WeatherLimits;
    private _emitRoutesChanged: () => void;
    private _hydraJobCount: number = 0;
    private _backend: Backends;

    private _vesselMovementCache: WeakMap<VoyageRoute, VesselMovementCacheEntry[]> = new WeakMap();

    /**
     * @name _fixupMaxSpeed
     * @description Users might have entered 0 as max speed, which will cause an error
     * in Hydra calculation. So remove `speed_maximum` from route when it is 0
    */
     private _fixupMaxSpeed(route: VoyageRoute) {
        for (const feature of route.route_geometry) {
            if (feature.properties.type === "LineString") continue;

            if (feature.properties.speed_maximum === 0) {
                delete feature.properties.speed_maximum;
            }
        }
    }

    private _loadWeatherLimits(): void
    {
        const savedObject = localStorage.getItem("voyage-routing-model-weather-limits");
        if (!savedObject)
        {
            this._weatherLimits = {
                Waves: { avoid: null, warn: null },
                Wind10M: { avoid: null, warn: null },
                Wind50M: { avoid: null, warn: null },
                Visibility: { avoid: null, warn: null },
                Icing: { avoid: null, warn: null },
                Hurricanes: { avoid: null, warn: null },
                TropicalStorms: { avoid: null, warn: null },
                Depressions: { avoid: null, warn: null },
                WindGusts10M: { avoid: null, warn: null },
                WindGusts50M: { avoid: null, warn: null },
                RiskWindSpeed: { avoid: null, warn: null },
                RiskWaveHeight: { avoid: null, warn: null },
                HeadWind: { avoid: null, warn: null },
                BeamWind: { avoid: null, warn: null },
                FollowingWind: { avoid: null, warn: null },
                BeamWavesHeight: { avoid: null, warn: null },
                BeamWavesPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                HeadWavesHeight: { avoid: null, warn: null },
                HeadWavesPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                FollowingWavesHeight: { avoid: null, warn: null },
                FollowingWavesPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                IceConcentration: { avoid: null, warn: null }
            };
            this.setWeatherLimits(this._weatherLimits);
        }
        else
        {
            this._weatherLimits = JSON.parse(savedObject);
        }
    }

    private async _postRoutingJobToRoutingAPI(inputRoute: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>
    {
        if (!options.routeType) {
            throw new Error("Missing routeType in input to postRoutingJob");
        }

        options.shipModel = options.shipModel || this.shipModel;
        options.draft = options.draft || Math.max(options.draftFore!, options.draftAft!);

        if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
            if (options.uiRouteIndex >= 0 && options.uiRouteIndex < this._uiRoutes.length) {
                this._uiRoutes[options.uiRouteIndex].status = "calculating";
                this._emitRoutesChanged();
            } else {
                console.warn("uiRouteIndex out of bounds!");
                delete options.uiRouteIndex;
            }
        }

        try {
            const jobId = await RoutingService.postRoutingJob(inputRoute, options);

            this._addJob({ jobId: jobId!, calcOptions: options });
            this._emitRoutesChanged();
            return await this._getRoutingAPIResult(jobId!, options, inputRoute);
        } catch (err: any) {
            if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan ) {
                this._uiRoutes[options.uiRouteIndex].status = "failed";
                this._uiRoutes[options.uiRouteIndex].error = err.message;
                this._emitRoutesChanged();
            }

            throw err;
        }
    }

    private async _postRoutingJobToOptimiser(route: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>
    {
        if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
            if (options.uiRouteIndex >= 0 && options.uiRouteIndex < this._uiRoutes.length) {
                this._setRouteAndStatus(route, "calculating", options.uiRouteIndex);
                this._emitRoutesChanged();
            } else {
                console.warn("uiRouteIndex out of bounds!");
                delete options.uiRouteIndex;
            }
        }

        const jobId = await RouteOptimizerService.postCalculationJob(route, {
            draftFore: options.draftFore || options.draft,
            draftAft: options.draftAft || options.draft,
            numberOfReefers: options.numberOfReefers ? options.numberOfReefers : 70,
            etd: options.etd,
            eta: options.eta,
            weatherLimits: options.weatherLimits,
            forecastVersion: options.forecastVersion,
            planScheduleId: options.planScheduleId,
            gmt: options.gmt
        });

        this._addJob({ jobId: jobId!, calcOptions: options });

        const result = await RouteOptimizerService.waitForJob(jobId!, options);

        this._removeJob(jobId!);

        if (result.status !== "complete")
        {
            if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
                this._uiRoutes[options.uiRouteIndex].status = "failed";
                this._uiRoutes[options.uiRouteIndex].error = result.statusDetails || null;
                this._emitRoutesChanged();
            }

            if (result.errorDetails && result.errorDetails.hydraRequestID)
            {
                throw new RouteOptimiserCalcError(result.errorDetails.hydraRequestID, result.errorDetails.hydraErrorBody, "Route calculation failed");
            }
            else
            {
                throw new Error(result.statusDetails);
            }
        }

        //Update zones flags
        if (result.route!.route_info) {
            result.route!.route_info.whaleZonesApplied = options.whaleZonesApplied;
        }

        if (options.passagePlan) {
            result.route!.passagePlan = options.passagePlan;
        }

        // Add created time, isPassedUpdate and uiType if needed
        result.route!.creationTime = new Date().toISOString();
        if (route.isPassedUpdate)
        {
            result.route!.isPassedUpdate = route.isPassedUpdate;
        }
        if (route.name)
        {
            result.route!.name = route.name;
        }
        if (options.uiRouteIndex !== undefined)
        {
            result.route!.uiType = `UserRoute${options.uiRouteIndex}`;
        }

        if (options.updatePlan)
        {
            this._updatePlanWithRoute(result.route!, options);
        }
        else if (options.addRouteToModel)
        {
            this.addRoute(result.route!);
        }

        return result.route!;
    }

    private async _postRoutingJobToHydra(route: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>
    {
        if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
            if (options.uiRouteIndex >= 0 && options.uiRouteIndex < this._uiRoutes.length) {
                this._setRouteAndStatus(route, "calculating", options.uiRouteIndex);
                this._emitRoutesChanged();
            } else {
                console.warn("uiRouteIndex out of bounds!");
                delete options.uiRouteIndex;
            }
        }

        const jobId = this._hydraJobCount++;
        this._addJob({ jobId: jobId, calcOptions: options });
        let result: VoyageRoute;

        try
        {
            result = await HydraRoutingService.postCalculationJob(route, {
                draftFore: options.draftFore || options.draft,
                draftAft: options.draftAft || options.draft,
                gmt: options.gmt,
                reeferConsumption: options.numberOfReefers ? options.numberOfReefers*3.5 : 250,
                etd: options.etd,
                eta: options.eta,
                forecastVersion: options.forecastVersion,
                imo: options.imo,
                maxSegmentDistance: options.maxSegmentDistance
            });
            
            const routeWeatherWorker = new RouteWeatherWorker(result, this._weatherLimits, false);
            await routeWeatherWorker.processRoute();
        }
        catch (err)
        {
            this._removeJob(jobId);
            
            throw err;
        }

        this._removeJob(jobId);

        //Update zones flags
        if (result.route_info) {
            result.route_info.whaleZonesApplied = options.whaleZonesApplied;
        }

        if (options.passagePlan) {
            result.passagePlan = options.passagePlan;
        }

        result.creationTime = new Date().toISOString();
        if (route.isPassedUpdate)
        {
            result.isPassedUpdate = route.isPassedUpdate;
        }
        if (route.name)
        {
            result.name = route.name;
        }
        if (options.uiRouteIndex !== undefined)
        {
            result.uiType = `UserRoute${options.uiRouteIndex}`;
        }

        if (options.updatePlan)
        {
            this._updatePlanWithRoute(result, options);
        }
        else if (options.addRouteToModel)
        {
            this.addRoute(result);
        }

        return result;
    }

    private _onPlanChanged(evt: PlanChangedEvent): void
    {
        if (evt.detail.scheduleId === this._activePlan)
        {
            this._updateRoutesFromPlan();
        }
    }

    private async _updateRoutesFromPlan(): Promise<void>
    {
        this._clearUIRoutes();
        if (this._activePlan)
        {
            const plan = await VesselPlanningModel!.getCurrentPlan(this._activePlan);
            if (plan)
            {
                for (let i = 0; i < plan.routes.length; i++)
                {
                    const route = plan.routes[i];

                    let index = uiTypeToIndex(route.uiType);
                    if (index >= 0 && index < this._uiRoutes.length && this._uiRoutes[index].routeIndex === -1)
                    {
                        this._setRouteAndStatus(route, "idle", index);
                        
                        // route calculation failed when the route info is missing from the results
                        if (!route.route_info && route.route_geometry) 
                        {  
                            this._setRouteAndStatus(route, "failed", index);
                        }
                        
                        await this._detectZones(route!, index);
                        this._uiRoutes[index].selected = (i === plan.selectedRouteIndex);
                    }
                }
            }
        }

        // Check if there are any (relevant) ongoing calculations
        for (const job of this._jobs)
        {
            if (job.calcOptions && job.calcOptions.uiRouteIndex !== undefined &&
                job.calcOptions.updatePlan && job.calcOptions.planScheduleId === this._activePlan)
            {
                this._uiRoutes[job.calcOptions.uiRouteIndex].status = "calculating";
            }
        }
    }

    private _clearUIRoutes(): void
    {
        for (let i = 0; i < this._uiRoutes.length; i++)
        {
            this._setRouteAndStatus(null, "idle", i);
        }
    }

    private _setRouteAndStatus(route: VoyageRoute|null, status: RouteCalcStatus, uiRouteIndex: number): void
    {
        if (uiRouteIndex >= this._uiRoutes.length || uiRouteIndex < 0) {
            console.warn("uiRouteIndex out of range!");
            return;
        }

        if (this._uiRoutes[uiRouteIndex].routeIndex >= 0)
        {
            this.deleteRoute(this._uiRoutes[uiRouteIndex].routeIndex);
        }
        this._uiRoutes[uiRouteIndex].status = status;

        if (status !== "failed") 
        {
            this._uiRoutes[uiRouteIndex].error = null;
        }

        this._uiRoutes[uiRouteIndex].routeIndex = route ? this.addRoute(route) : -1;
        if (route) {
            route.uiType = `UserRoute${uiRouteIndex}`;
        }
    }

    private async _updatePlanWithRoute(route: VoyageRoute, options: RoutingJobOptions): Promise<void>
    {
        const plan = await VesselPlanningModel!.getCurrentPlan(options.planScheduleId!);
        if (!plan)
        {
            console.warn("Could not find a plan for id ", options.planScheduleId);
            return;
        }

        if (route.uiType)
        {
            // Guard against saving more than one route with same uiType
            const currentIndex = plan.routes.findIndex( r => r.uiType === route.uiType );
            if (currentIndex !== -1)
            {
                options.updatePlan!.routeIndex = currentIndex;
            }
        }

        if (options.updatePlan!.routeIndex >= 0)
        {
            plan.routes[options.updatePlan!.routeIndex] = route;
        }
        else
        {
            plan.routes.push(route);
        }

        if (options.updatePlan!.selectRoute)
        {
            plan.selectedRouteIndex = options.updatePlan!.routeIndex >= 0 ? options.updatePlan!.routeIndex : plan.routes.length - 1;
        }

        await VesselPlanningModel!.setPlan(options.planScheduleId!, plan);

        if (options.updatePlan!.autoApprove && plan.selectedRouteIndex >= 0)
        {
            VesselPlanningModel!.approvePlan(options.planScheduleId!);
        }
    }

    private _createUIRoutes(count: number): void {
        for (let i = 0; i < count; i++) {
            this._uiRoutes.push({
                name: '',
                status: "idle",
                routeIndex: -1,
                error: null,
                selected: false,
                crossingWhaleZones: false
            });
        }
    }

    private _checkForURLJobs() {
        // Pull job_id off address and look it up
        const jobId = UiStateModel!.getValue('job_id')
        if (jobId) {
            UiStateModel!.setValue('job_id', null);
            this._addJob({ jobId });
            this._emitRoutesChanged();
            this._getRoutingAPIResult(jobId);
        }
    }

    private async _getRoutingAPIResult(jobId: string, options?: RoutingJobOptions, inputRoute?: VoyageRoute): Promise<VoyageRoute>
    {
        const result = await RoutingService.getResultForJob(jobId!, inputRoute);

        if (result && result.status === "Done" && options)
        {
            // Add the used calculation parameters (for the dynamic parts)
            // forecast_versions seems to be missing from the reply sometimes (sigh...) - default to the latest forecast if it is not there
            let forecast_versions = [WeatherModel!.latestForecastVersion];
            if (result.request.route_parameters.weather && result.request.route_parameters.weather.forecast_versions) {
                forecast_versions = result.request.route_parameters.weather.forecast_versions;
            }

            result.status_info.route_info.calculationInput = {
                forecast_versions: forecast_versions,
                draftAft: options.draftAft || options.draft,
                draftFore: options.draftFore || options.draft,
                etd: result.request.route_parameters.etd,
                eta: result.request.route_parameters.eta,
                weatherLimits: options.weatherLimits
            };

            if (options.routeType !== "ShortestDistance")
            {
                try {
                    const routeWeatherWorker = new RouteWeatherWorker(result.status_info, this._weatherLimits, false);
                    await routeWeatherWorker.processRoute();
                } catch (err: any) {
                    result.status = "failed";
                    result.status_info.message = err.message;
                }
            }

            if (options.passagePlan) {
                result.passagePlan = options.passagePlan;
            }
        }

        // Remove from list again
        this._removeJob(jobId);

        if (!result || result.status !== "Done") {
            const error = result && result.status_info && result.status_info.message ? result.status_info.message : "Unknown error occurred";

            if (options && options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
                const uiRoute = this._uiRoutes[options.uiRouteIndex];
                uiRoute.status = "failed";
                uiRoute.error = error;
            }

            this._emitRoutesChanged();
            throw new Error(error);
        }

        const route = result.status_info;

        // Add created time and isPassedUpdate
        route.creationTime = new Date().toISOString();
        if (inputRoute && inputRoute.isPassedUpdate)
        {
            route.isPassedUpdate = inputRoute.isPassedUpdate;
        }

        if (options) {
            // Add uiType if needed
            if (options.uiRouteIndex !== undefined)
            {
                route.uiType = `UserRoute${options.uiRouteIndex}`;
            }

            if (options.updatePlan)
            {
                this._updatePlanWithRoute(route, options);
            }
            else if (options.addRouteToModel)
            {
                this.addRoute(route);
            }

        } else {
            this.addRoute(route);
        }

        return route;
    }

    private _addJob(job: JobDetails)
    {
        this._jobs.push(job);
        this.dispatchEvent(new Event('jobs-changed'));
    }

    private _removeJob(jobId: string|number)
    {
        this._jobs = this._jobs.filter( jobDetails => jobDetails.jobId !== jobId );
        this.dispatchEvent(new Event('jobs-changed'));
    }

    private _findGeoJsonPoint(features: (VoyageRoutePointFeature|VoyageRouteLineFeature)[],
                              coords: [number, number], indexHint: number): VoyageRoutePointFeature|null
    {
        // Try the indexHint first, then search through the entire array
        if (features[indexHint].geometry.type === "Point" &&
            features[indexHint].geometry.coordinates[0] === coords[0] && features[indexHint].geometry.coordinates[1] === coords[1])
        {
            return <VoyageRoutePointFeature>features[indexHint];
        }

        for (const feature of features)
        {
            if (feature.geometry.type === "Point" &&
                feature.geometry.coordinates[0] === coords[0] && feature.geometry.coordinates[1] === coords[1])
            {
                return <VoyageRoutePointFeature>feature;
            }
        }

        console.warn("Could not find a Point with coordinates", coords);
        return null;
    }

    private _createVesselMovementCacheGeoJson(route: VoyageRoute)
    {
        const cache: VesselMovementCacheEntry[] = [];

        for (let i = 0; i < route.route_geometry.length; i++)
        {
            const feature = route.route_geometry[i];

            if (feature.geometry.type === "LineString")
            {
                // Historical WPs are not sent to Hydra and thus not re-calculated. This means
                // timestamps on historical WPs will never change - even when vessel changes the route.
                // If vessel changes ETA during voyage, the historical timestamps can be a lot
                // behind vessel, and thus vessel in time icon can be a lot befind vessel icon.
                // To fix this, skip isPassed linestrings, so vessel in time icon is following
                // updated, future timestamps.
                if (i > 0 && route.route_geometry[i-1].properties.isPassed) {
                    continue;
                }

                const endCoords = feature.geometry.coordinates[1];
                const endPoint = this._findGeoJsonPoint(route.route_geometry, endCoords, i+1);

                if (feature.properties.informationalWaypoints)
                {
                    for (let i = 0; i < feature.properties.informationalWaypoints.length; i++)
                    {
                        const wp = feature.properties.informationalWaypoints[i];
                        const nextWP = i < feature.properties.informationalWaypoints.length - 1 ? feature.properties.informationalWaypoints[i+1] : endPoint;

                        const startDate = wp.properties.timestamp!;
                        const endDate = nextWP.properties.timestamp!;

                        const line = getLineStringForFractionPositions({
                            type: "Feature",
                            geometry: { type: "LineString", coordinates: [wp.geometry.coordinates, nextWP.geometry.coordinates] },
                            properties: { leg_type: feature.properties.leg_type }
                        });
                            
                        const projectedLine = <LineString>line.clone();
                        projectedLine.transform('EPSG:4326', _PROJECTION);

                        cache.push({
                            startDate: new Date(startDate).getTime(),
                            endDate: new Date(endDate).getTime(),
                            line: line,
                            projectedLine
                        });
                    }
                }
                else
                {
                    const startCoords = feature.geometry.coordinates[0];
                    const startPoint = this._findGeoJsonPoint(route.route_geometry, startCoords, i-1);
                    const startDate = startPoint!.properties?.timestamp! ? startPoint!.properties.timestamp! : route?.route_info?.etd || ''; // Start Point seems to be missing timestamp
                    const endDate = endPoint!.properties?.timestamp!;
    
                    const line = getLineStringForFractionPositions(<VoyageRouteLineFeature>feature);
    
                    const projectedLine = <LineString>line.clone();
                    projectedLine.transform('EPSG:4326', _PROJECTION);
    
                    const result = {
                        startDate: new Date(startDate).getTime(),
                        endDate: new Date(endDate).getTime(),
                        line: line,
                        projectedLine
                    };
    
                    cache.push(result);    
                }
            }
        }

        this._vesselMovementCache.set(route, cache);
    }

    // Check if the route is crossing any zones
    private async _detectZones(route: VoyageRoute, uiRouteIndex: number) {
        this._uiRoutes[uiRouteIndex].crossingWhaleZones = await (new WhaleZonesUtilities().routeCrossingZones(route.route_geometry));
    }

    async saveRoute (route: VoyageRoute, uiRouteIndex: number, scheduleId: string) {
        const plan = await VesselPlanningModel!.getCurrentPlan(scheduleId);
        if (!plan) {
            console.warn(`No plan found for ${scheduleId}, changes will be lost!`);
            return;
        }

        // Overwrite any route already present with the given uiRouteIndex
        let planIndex = plan.routes.findIndex( route => route.uiType === `UserRoute${uiRouteIndex}`);

        // Save route now (so we don't lose it if the calculation fails for some reason)
        const wasSelected = planIndex >= 0 && plan.selectedRouteIndex === planIndex;
        let oldRoute: VoyageRoute|null = null;
        route.uiType = `UserRoute${uiRouteIndex}`;

        if (planIndex >= 0) {
            oldRoute = plan.routes[planIndex] || null;
            route.passagePlan = plan.routes[planIndex].passagePlan!;
            plan.routes[planIndex] = route;
            if (wasSelected) {
                plan.selectedRouteIndex = -1;
            }
        } else {
            route.passagePlan = {
                description: "",
                reminders: "",
                notEcdisCompliant: false,
                encCellsUsed: "",
                booksAndTables: "",
                chartsAndLightsNotes: "",
                sailingDirections: "",
                listOfRadioSignals: "",
                listOfLights: "",
                gpsEcdisRadarMap: "",
                paperCharts: "",
                pubCorrectedToWeek: "",
                tideTables: "",
                publicationsNotes: "",
                mastersOrders: "",
                safetyMessages: {
                    navareaRegions: "",
                    navtexStations: ""
                },
                weatherInfo: {
                    navareaRegions: "",
                    navtexStations: "",
                    info: ""
                },
                equipmentStatus: {
                    operationalCondition: "",
                    failures: ""
                },
                weatherRouting: {
                    required: false,            
                    acknowledged: false,     
                    pilotPointAcknowledged: false 
                }
            }

            plan.routes.push(route);
            planIndex = plan.routes.length-1;
        }

        await this._syncPilotETAs(plan, route);

        // update sailing directions when the route has changed
        if (oldRoute) {
            const routeChanged = VesselPlanningModel!.passagePlanRouteChanged(oldRoute.route_geometry, route.route_geometry);
            
            if (routeChanged) {
                let weatherRouting = plan.routes[planIndex].passagePlan?.weatherRouting;
                if (weatherRouting) {
                    weatherRouting = {required: false, acknowledged: false,pilotPointAcknowledged: false }
                }
                
                plan.passagePlan.sailingDirections = VesselEnpBookAndChapterModel!.getCrossedEnpChaptersAndBooks(route.route_geometry); 
            }
        }

        // Keep approval if passage plan is not affected (ie. only navigational waypoints have been changed)
        let autoApprove = false;
        if (plan.status === "approved" && wasSelected && oldRoute)
        {
            autoApprove = VesselPlanningModel!.passagePlanRouteChanged(oldRoute.route_geometry, route.route_geometry) === false;
        }
        if (plan.status === "approved" && !autoApprove)
        {
            const popupText= `Earlier approved plan have been detected as modified; to ensure correct plan is handled; please approve and send the updated plan.`
            VesselPlanningModel!.approvalReminder(popupText);
        }
        await VesselPlanningModel!.setPlan(scheduleId, plan);

        const options: RoutingJobOptions = {
            draftFore: plan.foreDraft,
            draftAft: plan.aftDraft,
            gmt: plan.gmt !== null ? plan.gmt : undefined,
            etd: plan.etd,
            eta: plan.eta,
            numberOfReefers: plan.numberOfReefers,
            routeType: "WeatherOptimized",
            uiRouteIndex: uiRouteIndex,
            planScheduleId: scheduleId,
            updatePlan: {
                routeIndex: planIndex,
                selectRoute: wasSelected,
                autoApprove: autoApprove
            },
            whaleZonesApplied: false, // if the route has been edited, remove the zone flags
            passagePlan: route.passagePlan
        };
        // Trigger calculation, but don't wait for the result
        this.postRoutingJobUsingRoute(route, options); //uncoment this part 
    }

    private async _syncPilotETAs(plan: VoyagePlan, updatedRoute: VoyageRoute)
    {
        const updatedPilotPoints = updatedRoute.route_geometry.filter( feature => feature.geometry.type === "Point" && feature.properties.pilotPoint );
        if (updatedPilotPoints.length < 1) {
            return;
        }
        const newDeparturePilot = (<VoyageRoutePointFeature[]>updatedPilotPoints).length > 1 ? updatedPilotPoints[0] : null;
        const newArrivalPilot = (<VoyageRoutePointFeature[]>updatedPilotPoints).length > 0 ? updatedPilotPoints[updatedPilotPoints.length-1] : null;
        const newDeparturePilotEta = newDeparturePilot ? newDeparturePilot.properties.eta : null;
        const newArrivalPilotEta = newArrivalPilot ? newArrivalPilot.properties.eta : null;
        
        // check for pilot ETA conflicts between updatedRoute and plan
        const departureHasChanged = newDeparturePilotEta
                                    && plan.departurePilotEtd > 0
                                    && plan.departurePilotEtd !== Date.parse(newDeparturePilotEta);
        const arrivalHasChanged =   newArrivalPilotEta
                                    && plan.arrivalPilotEta > 0
                                    && plan.arrivalPilotEta !== Date.parse(newArrivalPilotEta);
        
       

        let useNewPilotInfo = false;
        /** This part was moved from voyage-edit-route */
        // new pilot ETAs deviate from plan pilot ETAs
        if (departureHasChanged || arrivalHasChanged) {
            const dialog = VoyageDialogPopupManager.createDialog({
                headerText: "Pilot ETA Conflict",
                bodyText: `${departureHasChanged ? "Departure" : ""} ${departureHasChanged && arrivalHasChanged ? "and" : ""} ${arrivalHasChanged ? "Arrival" : ""} pilot time; ${departureHasChanged && arrivalHasChanged ? "are" : "is"} different from what is defined in Schedule View.
                Do you want to use what is entered here?
                ${departureHasChanged ? `\nDeparture Pilot off in Schedule Editor ${GetFormattedLocalDate(new Date(plan.departurePilotEtd).toISOString())} UTC
                Route Editor input ${GetFormattedLocalDate(newDeparturePilotEta!)} UTC` : ""}
                ${arrivalHasChanged ? `\nArrival Pilot off in Schedule Editor ${GetFormattedLocalDate(new Date(plan.arrivalPilotEta).toISOString())} UTC
                Route Editor input ${GetFormattedLocalDate(newArrivalPilotEta!)} UTC` : ""}`,
                acceptButtonText: "Confirm",
                rejectButtonText: "Cancel"
            });

            const response = await dialog.getResponse();
            if (response === "accept") {
                useNewPilotInfo = true;
            }
        }
        
        // Plan is single source of truth for pilot ETAs, which is used to sync routes pilot ETAs.
        // Therefore, plan should be updated first, then routes
        let planPilotETAsHaveChanged = false;
        if (newArrivalPilotEta || newDeparturePilotEta) {
            planPilotETAsHaveChanged = this._updatePlanPilotInfo(plan, newDeparturePilotEta, newArrivalPilotEta, useNewPilotInfo);
        }
        // no new pilot ETAs, so just update any missing pilot ETAs on the updated route
        else if (((!newDeparturePilotEta && plan.departurePilotEtd !== -1) || (!newArrivalPilotEta && plan.arrivalPilotEta !== -1))) {
            this._updateNewRoutePilotETAs(plan, <VoyageRoutePointFeature>newDeparturePilot, <VoyageRoutePointFeature>newArrivalPilot);
            return;
        }
        if (planPilotETAsHaveChanged) {
            this._updateAllRoutesPilotETAs(plan);
        } else {
            // plan has not changed, but new route might still have pilot ETAs that need to be updated
            this._updateNewRoutePilotETAs(plan, <VoyageRoutePointFeature>newDeparturePilot, <VoyageRoutePointFeature>newArrivalPilot);
        }
    }

    private _updateNewRoutePilotETAs(plan: VoyagePlan, departurePilot: VoyageRoutePointFeature, arrivalPilot: VoyageRoutePointFeature) {
        if (plan.departurePilotEtd !== -1 && departurePilot) {
            departurePilot.properties.eta = new Date(plan.departurePilotEtd).toISOString();
        }
        if (plan.arrivalPilotEta !== -1 && arrivalPilot) {
            arrivalPilot.properties.eta = new Date(plan.arrivalPilotEta).toISOString();
        }
    }

    private _updateAllRoutesPilotETAs(plan: VoyagePlan) {
        for (const route of plan.routes) {
            const pilotIndeces: number[] = [];
            for (let i = 0; i < route.route_geometry.length; i++)
            {
                const feature = route.route_geometry[i];
                if (feature.geometry.type === "Point" && feature.properties.pilotPoint)
                {
                    pilotIndeces.push(i);
                }
            }

            const departurePilot = pilotIndeces.length > 1 ? <VoyageRoutePointFeature>route.route_geometry[pilotIndeces[0]] : null;
            const arrivalPilot = pilotIndeces.length > 0 ? <VoyageRoutePointFeature>route.route_geometry[pilotIndeces[pilotIndeces.length-1]] : null;

            if (departurePilot) {
                if (plan.departurePilotEtd !== -1) {
                    departurePilot.properties.eta = new Date(plan.departurePilotEtd).toISOString();
                } else {
                    delete departurePilot.properties.eta;
                }
            }
            if (arrivalPilot) {
                if (plan.arrivalPilotEta !== -1) {
                    arrivalPilot.properties.eta = new Date(plan.arrivalPilotEta).toISOString();
                } else {
                    delete arrivalPilot.properties.eta;
                }
            }
        }
    }

    private _updatePlanPilotInfo(plan: VoyagePlan, newDeparturePilotEta: string, newArrivalPilotEta: string, useNewPilotInfo: boolean) {
        let planPilotETAsHaveChanged = false;
        // handle missing pilot ETAs on plan
        if (plan.departurePilotEtd < 0 && newDeparturePilotEta) {
            plan.departurePilotEtd = Date.parse(newDeparturePilotEta);
            planPilotETAsHaveChanged = true;
        }
        if (plan.arrivalPilotEta < 0 && newArrivalPilotEta) {
            plan.arrivalPilotEta = Date.parse(newArrivalPilotEta);
            planPilotETAsHaveChanged = true;
        }

        // handle pilot ETA conflicts between route and plan
        if (useNewPilotInfo) {
            if (plan.departurePilotEtd !== -1 && newDeparturePilotEta && plan.departurePilotEtd !== Date.parse(newDeparturePilotEta)) {
                plan.departurePilotEtd = Date.parse(newDeparturePilotEta);
                planPilotETAsHaveChanged = true;
            } 
            if (plan.arrivalPilotEta !== -1 && newArrivalPilotEta && plan.arrivalPilotEta !== Date.parse(newArrivalPilotEta)) {
                plan.arrivalPilotEta = Date.parse(newArrivalPilotEta);
                planPilotETAsHaveChanged = true;
            }
        }

        return planPilotETAsHaveChanged;
    }
    // async voyageSummary(payload:VoyageSummaryPayload): Promise<VoyageSummaryReportData | null>{
    //     let data  = VoyageSummaryService.getSummaryReport(payload);
    //     return data;
    // }
    async routeTypeFilter(payload: RouteTypesPayload[]){
        const data  = PlansService.getRouteTypes(payload);
        return data;
    }
};
